import { motion } from "framer-motion";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useClickOutside, useIsMobile } from "@utils/hooks";
import { PrimaryBold } from "@ui/components/containers/primary";
import { row } from "@ui/components/layout";

interface ColorSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
	color: string;
	onColorChange: (color: string) => void;
	colors: readonly string[];
}
export const ColorSelector: React.FC<ColorSelectorProps> = ({ color, colors, onColorChange, ...htmlProps }) => {
	const colorEditorRef = useClickOutside(() => {
		setOpened(false);
	});
	const [opened, setOpened] = useState(false);
	const isMobile = useIsMobile();

	const container = {
		hidden: {},
		show: {
			transition: {
				staggerChildren: 0.1,
			},
		},
	};
	const item = {
		hidden: { opacity: 0, x: -40, scale: 0.5 },
		show: { opacity: 1, x: 0, scale: 1, transition: { type: "tween" as const } },
	};

	return (
		<div {...htmlProps}>
			<Content
				ref={colorEditorRef}
				onClick={() => {
					setOpened(!opened);
				}}
				onMouseEnter={isMobile ? undefined : () => setOpened(true)}
				onMouseLeave={isMobile ? undefined : () => setOpened(false)}
				variants={container}
				initial="hidden"
				animate={opened ? "show" : "hidden"}
			>
				<ColorView color={color} />
				{opened ? (
					colors
						.filter((c) => c !== color)
						.map((c) => (
							<ColorView
								onClick={() => {
									onColorChange(c);
								}}
								key={c}
								color={c}
								variants={item}
							/>
						))
				) : (
					<PrimaryBold>{I18n.t("workspace.color.label")}</PrimaryBold>
				)}
			</Content>
		</div>
	);
};

const Content = styled(motion.div)`
	cursor: pointer;
	${row(10, "flex-start", "center")};
`;

const ColorView = styled(motion.div)<{ color: string }>`
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	border-radius: 20px;
	background-color: ${({ color }) => color};
`;
